import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const steps = [
  {
    id: 'welcome',
    title: 'Welcome to Agent GG',
    description: 'Your AI assistant for natural conversations and helpful information.'
  },
  {
    id: 'goals',
    title: 'What are your goals?',
    description: 'Help us understand what you want to achieve with Agent GG.'
  },
  {
    id: 'preferences',
    title: 'Your Preferences',
    description: 'Tell us about your preferences to personalize your experience.'
  },
  {
    id: 'uploads',
    title: 'Upload Your Documents',
    description: 'Upload or link to relevant documents to help initialize the context for decision-making.'
  },
  {
    id: 'knowledge-base',
    title: 'Company Knowledge Base',
    description: 'Upload or link to documents for your company knowledge base (optional).'
  }
];

const Onboarding = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [goals, setGoals] = useState('');
  const [preferences, setPreferences] = useState('');
  const [resumeFiles, setResumeFiles] = useState<File[]>([]);
  const [resumeLinks, setResumeLinks] = useState<string[]>([]);
  const [newResumeLink, setNewResumeLink] = useState('');
  const [teamInfoFiles, setTeamInfoFiles] = useState<File[]>([]);
  const [teamInfoLinks, setTeamInfoLinks] = useState<string[]>([]);
  const [newTeamInfoLink, setNewTeamInfoLink] = useState('');
  const [teamCharterFiles, setTeamCharterFiles] = useState<File[]>([]);
  const [teamCharterLinks, setTeamCharterLinks] = useState<string[]>([]);
  const [newTeamCharterLink, setNewTeamCharterLink] = useState('');
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState<File[]>([]);
  const [knowledgeBaseLinks, setKnowledgeBaseLinks] = useState<string[]>([]);
  const [newKnowledgeBaseLink, setNewKnowledgeBaseLink] = useState('');
  const { completeOnboarding } = useAuth();
  const navigate = useNavigate();

  const resumeInputRef = useRef<HTMLInputElement>(null);
  const teamInfoInputRef = useRef<HTMLInputElement>(null);
  const teamCharterInputRef = useRef<HTMLInputElement>(null);
  const knowledgeBaseInputRef = useRef<HTMLInputElement>(null);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
      navigate('/chat');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
    navigate('/chat');
  };

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files);
      setFiles(prevFiles => [...prevFiles, ...newFiles]);
      // In a real app, you would upload the files to a server here
      console.log("Files selected:", newFiles.map(f => f.name).join(", "));
    }
  };

  const removeFile = (index: number, files: File[], setFiles: React.Dispatch<React.SetStateAction<File[]>>) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const handleAddLink = (
    link: string,
    links: string[],
    setLinks: React.Dispatch<React.SetStateAction<string[]>>,
    setNewLink: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (link.trim() !== '') {
      setLinks([...links, link.trim()]);
      setNewLink('');
    }
  };

  const removeLink = (index: number, links: string[], setLinks: React.Dispatch<React.SetStateAction<string[]>>) => {
    const newLinks = [...links];
    newLinks.splice(index, 1);
    setLinks(newLinks);
  };

  const renderFileList = (files: File[], setFiles: React.Dispatch<React.SetStateAction<File[]>>) => {
    return (
      <div className="mt-4">
        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-300">Uploaded files:</p>
            <ul className="bg-tertiary rounded-md p-2 max-h-32 overflow-y-auto">
              {files.map((file, index) => (
                <li key={index} className="flex justify-between items-center text-sm text-gray-300 py-1 px-2 hover:bg-gray-700 rounded">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {file.name}
                  </div>
                  <button
                    onClick={() => removeFile(index, files, setFiles)}
                    className="text-gray-400 hover:text-gray-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderLinkList = (links: string[], setLinks: React.Dispatch<React.SetStateAction<string[]>>) => {
    return (
      <div className="mt-4">
        {links.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-300">Added links:</p>
            <ul className="bg-tertiary rounded-md p-2 max-h-32 overflow-y-auto">
              {links.map((link, index) => (
                <li key={index} className="flex justify-between items-center text-sm text-gray-300 py-1 px-2 hover:bg-gray-700 rounded">
                  <div className="flex items-center truncate max-w-[90%]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <a href={link} target="_blank" rel="noopener noreferrer" className="truncate hover:text-accent">
                      {link}
                    </a>
                  </div>
                  <button
                    onClick={() => removeLink(index, links, setLinks)}
                    className="text-gray-400 hover:text-gray-200 ml-2 flex-shrink-0"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderLinkInput = (
    value: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    links: string[],
    setLinks: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    return (
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-300 mb-2">Add a link (Google Docs, Sheets, etc):</p>
        <div className="flex gap-2">
          <input
            type="url"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-3 py-2 bg-tertiary text-white rounded-md border border-border-color focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button
            onClick={() => handleAddLink(value, links, setLinks, setValue)}
            className="px-3 py-2 bg-accent hover:bg-accent/90 transition-colors rounded-md text-white"
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case 'welcome':
        return (
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-accent flex items-center justify-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 text-white">
                <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
              </svg>
            </div>
            <p className="text-lg text-gray-300 mt-4">
              Let's set up your experience to make Agent GG more personalized and effective for your needs.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              You can skip any step of this process and customize these settings later.
            </p>
          </div>
        );

      case 'goals':
        return (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-300">
              What are your main goals for using Agent GG?
            </label>
            <textarea
              value={goals}
              onChange={(e) => setGoals(e.target.value)}
              rows={5}
              className="w-full bg-tertiary text-white rounded-md border border-border-color p-3 focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="E.g., Project management, personal productivity, team coordination..."
            ></textarea>
            <p className="text-xs text-gray-400">
              Your goals help us tailor responses to better suit your needs.
            </p>
          </div>
        );

      case 'preferences':
        return (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-300">
              Any specific preferences or requirements?
            </label>
            <textarea
              value={preferences}
              onChange={(e) => setPreferences(e.target.value)}
              rows={5}
              className="w-full bg-tertiary text-white rounded-md border border-border-color p-3 focus:outline-none focus:ring-1 focus:ring-accent"
              placeholder="E.g., Communication style, data format preferences, integration with tools like ClickUp or Excel..."
            ></textarea>
            <p className="text-xs text-gray-400">
              Your preferences help us personalize your experience.
            </p>
          </div>
        );

      case 'uploads':
        return (
          <div className="space-y-6">
            {/* Resume Upload */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Upload your resume(s)
                </label>
                <span className="text-xs text-gray-400">
                  Accepted: PDF, DOC, DOCX, CSV, XLSX
                </span>
              </div>

              <div className="flex gap-3">
                <div
                  onClick={() => resumeInputRef.current?.click()}
                  className="border-2 border-dashed border-border-color rounded-md p-4 text-center cursor-pointer hover:border-accent transition-colors flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-400">Upload file</p>
                  <input
                    ref={resumeInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.doc,.csv,.xlsx"
                    multiple
                    onChange={(e) => handleFileUpload(e, setResumeFiles)}
                  />
                </div>
              </div>

              {renderFileList(resumeFiles, setResumeFiles)}
              {renderLinkInput(newResumeLink, setNewResumeLink, resumeLinks, setResumeLinks)}
              {renderLinkList(resumeLinks, setResumeLinks)}
            </div>

            {/* Team Info Upload */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Upload team information
                </label>
                <span className="text-xs text-gray-400">
                  Accepted: PDF, DOC, DOCX, CSV, XLSX
                </span>
              </div>

              <div className="flex gap-3">
                <div
                  onClick={() => teamInfoInputRef.current?.click()}
                  className="border-2 border-dashed border-border-color rounded-md p-4 text-center cursor-pointer hover:border-accent transition-colors flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-400">Upload file</p>
                  <input
                    ref={teamInfoInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.doc,.csv,.xlsx"
                    multiple
                    onChange={(e) => handleFileUpload(e, setTeamInfoFiles)}
                  />
                </div>
              </div>

              {renderFileList(teamInfoFiles, setTeamInfoFiles)}
              {renderLinkInput(newTeamInfoLink, setNewTeamInfoLink, teamInfoLinks, setTeamInfoLinks)}
              {renderLinkList(teamInfoLinks, setTeamInfoLinks)}
            </div>

            {/* Team Charter Upload */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Upload team charter (optional)
                </label>
                <span className="text-xs text-gray-400">
                  Accepted: PDF, DOC, DOCX, CSV, XLSX
                </span>
              </div>

              <div className="flex gap-3">
                <div
                  onClick={() => teamCharterInputRef.current?.click()}
                  className="border-2 border-dashed border-border-color rounded-md p-4 text-center cursor-pointer hover:border-accent transition-colors flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-400">Upload file</p>
                  <input
                    ref={teamCharterInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.doc,.csv,.xlsx"
                    multiple
                    onChange={(e) => handleFileUpload(e, setTeamCharterFiles)}
                  />
                </div>
              </div>

              {renderFileList(teamCharterFiles, setTeamCharterFiles)}
              {renderLinkInput(newTeamCharterLink, setNewTeamCharterLink, teamCharterLinks, setTeamCharterLinks)}
              {renderLinkList(teamCharterLinks, setTeamCharterLinks)}
            </div>
          </div>
        );

      case 'knowledge-base':
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-300">
                  Upload company knowledge base
                </label>
                <span className="text-xs text-gray-400">
                  Accepted: PDF, DOC, DOCX, CSV, XLSX
                </span>
              </div>

              <div className="flex gap-3">
                <div
                  onClick={() => knowledgeBaseInputRef.current?.click()}
                  className="border-2 border-dashed border-border-color rounded-md p-4 text-center cursor-pointer hover:border-accent transition-colors flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-400">Upload file</p>
                  <input
                    ref={knowledgeBaseInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.docx,.doc,.csv,.xlsx"
                    multiple
                    onChange={(e) => handleFileUpload(e, setKnowledgeBaseFiles)}
                  />
                </div>
              </div>

              {renderFileList(knowledgeBaseFiles, setKnowledgeBaseFiles)}
              {renderLinkInput(newKnowledgeBaseLink, setNewKnowledgeBaseLink, knowledgeBaseLinks, setKnowledgeBaseLinks)}
              {renderLinkList(knowledgeBaseLinks, setKnowledgeBaseLinks)}

              <p className="text-xs text-gray-400 mt-2">
                This helps initialize the agent's context for decision-making.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-secondary">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-gray-700">
        <div
          className="h-full bg-accent transition-all duration-300 ease-in-out"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        ></div>
      </div>

      <div className="flex-grow flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md mx-auto">
          {/* Step indicator */}
          <div className="flex justify-between mb-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`h-2 w-2 rounded-full ${
                  index < currentStep ? 'bg-accent' :
                  index === currentStep ? 'bg-accent' : 'bg-gray-600'
                }`}
              ></div>
            ))}
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-4">{steps[currentStep].title}</h2>
              <p className="text-gray-400 mb-6">{steps[currentStep].description}</p>

              <div className="mt-6">
                {renderStepContent()}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation buttons */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-gray-100 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleSkip}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-300 hover:text-gray-100 hover:bg-gray-700"
              >
                Skip All
              </button>

              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 rounded-xl font-medium text-white bg-accent hover:bg-accent/90 transition-all duration-200"
              >
                {currentStep < steps.length - 1 ? 'Next' : 'Get Started'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
